import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Camera, Loader2, Sprout } from "lucide-react";
import type { Item, User } from "@shared/schema";

type SellerItem = Item & { seller?: User | null };

type GrowthPhoto = {
  id: string;
  itemId: string;
  photoUrl: string;
  note: string | null;
  estimatedRipeDate: string | null;
  takenAt: string;
};

function PhotoUploader({ item }: { item: SellerItem }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [note, setNote] = useState("");
  const [newRipeDate, setNewRipeDate] = useState(
    item.estimatedRipeDate
      ? new Date(item.estimatedRipeDate).toISOString().slice(0, 10)
      : "",
  );
  const [uploading, setUploading] = useState(false);

  const { data: photos = [] } = useQuery<GrowthPhoto[]>({
    queryKey: ["/api/items", item.id, "growth-photos"],
    queryFn: async () => {
      const res = await fetch(`/api/items/${item.id}/growth-photos`, {
        credentials: "include",
      });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const upRes = await fetch("/api/uploads/item-image", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!upRes.ok) throw new Error(await upRes.text());
      const { imageUrl } = await upRes.json();

      const ripeIso = newRipeDate ? new Date(newRipeDate).toISOString() : null;
      const res = await fetch(`/api/items/${item.id}/growth-photos`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          photoUrl: imageUrl,
          note: note.trim() || null,
          estimatedRipeDate: ripeIso,
        }),
      });
      if (!res.ok) throw new Error(await res.text());

      queryClient.invalidateQueries({
        queryKey: ["/api/items", item.id, "growth-photos"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/items"] });
      setNote("");
      toast({ title: "Photo posted", description: "Buyers will see it on the listing." });
    } catch (err: any) {
      toast({
        title: "Upload failed",
        description: err?.message ?? "Try again",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const lastPhoto = photos[0];
  const ripeDate = item.estimatedRipeDate ? new Date(item.estimatedRipeDate) : null;
  const daysSinceLast = lastPhoto
    ? Math.floor((Date.now() - new Date(lastPhoto.takenAt).getTime()) / 86_400_000)
    : null;

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-start gap-3">
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt={item.name}
              className="h-16 w-16 rounded-lg object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="font-semibold">{item.name}</div>
            {ripeDate && (
              <div className="text-xs text-emerald-800">
                Ripe ETA: {ripeDate.toLocaleDateString()}
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              {daysSinceLast == null
                ? "No photos yet — post one to start"
                : daysSinceLast >= 7
                  ? `Last photo ${daysSinceLast} days ago — time for an update`
                  : `Last photo ${daysSinceLast} day${daysSinceLast === 1 ? "" : "s"} ago`}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Color is starting to break — about a week away"
              className="min-h-[60px] text-xs"
            />
          </div>
          <div>
            <Label className="text-xs">Update ripe date (optional)</Label>
            <Input
              type="date"
              value={newRipeDate}
              onChange={(e) => setNewRipeDate(e.target.value)}
              className="text-xs"
            />
          </div>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={fileInputRef}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
              e.target.value = "";
            }}
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full"
          >
            {uploading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Camera className="mr-2 h-4 w-4" />
            )}
            Post weekly photo
          </Button>
        </div>

        {photos.length > 0 && (
          <div className="space-y-1.5 pt-1">
            <div className="text-[10px] uppercase font-semibold text-muted-foreground">
              History
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {photos.map((photo) => (
                <div key={photo.id} className="shrink-0 space-y-1">
                  <img
                    src={photo.photoUrl}
                    alt="Growth"
                    className="h-20 w-20 rounded object-cover"
                  />
                  <div className="text-[9px] text-muted-foreground">
                    {new Date(photo.takenAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SellerGrowthPhotos() {
  const [, setLocation] = useLocation();
  const { data: items = [], isLoading } = useQuery<SellerItem[]>({
    queryKey: ["/api/seller/items"],
    queryFn: async () => {
      const res = await fetch("/api/seller/items", { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const growingItems = items.filter((item: any) => item.growthState === "growing");

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header cartCount={0} onCartClick={() => {}} />
      <main className="flex-1">
        <div className="container mx-auto max-w-2xl px-4 py-5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/seller")}
            className="mb-3"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to dashboard
          </Button>

          <div className="mb-4 flex items-center gap-2">
            <Sprout className="h-5 w-5 text-emerald-700" />
            <h1 className="text-xl font-bold">Growing items</h1>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : growingItems.length === 0 ? (
            <Card>
              <CardContent className="space-y-3 pt-5 text-sm text-muted-foreground">
                <p>
                  Only produce you've marked <strong>Still growing</strong> (not
                  &ldquo;Ready now&rdquo;) shows up here so you can post weekly progress
                  photos.
                </p>
                <p>
                  Already listed produce that's still on the plant? Edit the listing and
                  switch it to &ldquo;Still growing.&rdquo;
                </p>
                <Button variant="outline" size="sm" onClick={() => setLocation("/seller")}>
                  Edit my listings
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {growingItems.map((item) => (
                <PhotoUploader key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
